/**
 * Learn.jsx — landing page for the evidence-based reading-skills drills
 * catalogue. Ships MVP+ per Feb 13 2026 user spec: 3 fully-interactive
 * drills (Chunking, Pacer, Timed passage + comprehension quiz) and 5
 * "Coming soon" cards so users see the full path.
 *
 * The single unifying metric across every drill is COMPREHENSION-ADJUSTED
 * WPM = raw_wpm × (comprehension_pct / 100). Displayed prominently so
 * users don't optimise for skimming.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, Layers, Gauge, Search, BookOpenCheck, Eye,
  Sparkles, Type as TypeIcon, TrendingUp, Trophy, Lock, ChevronRight,
  Wand2, X, Loader2, History, Trash2, Shuffle,
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';
import CustomPassageModal from '../components/CustomPassageModal';
import TrialGateBanner from '../components/TrialGateBanner';
import useTrialGate from '../hooks/useTrialGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon per drill + a stable ordering so users always find the same
// tile in the same place. `route` is null for the 5 "Coming soon"
// cards; `unlockDescription` shows the evidence-based framing.
const DRILLS = [
  {
    key: 'chunking',
    title: 'Chunking Drills',
    tagline: '2–4 word phrase groups',
    icon: Layers,
    accent: 'cyan',
    route: '/learn/chunking',
    body: 'Read in phrase blocks instead of one word at a time. Widens your eye-span — the most evidence-backed lever for faster reading with retained comprehension.',
    gamified: 'Unlock wider chunk sizes as your adjusted WPM climbs.',
  },
  {
    key: 'pacer',
    title: 'Pacer-Guided Reading',
    tagline: 'Beat the moving highlight',
    icon: Gauge,
    accent: 'fuchsia',
    route: '/learn/pacer',
    body: 'A highlight sweeps through the text at a set WPM, slightly faster than your baseline. You read along; the pacer never regresses so you can\'t either.',
    gamified: 'Small WPM increases each session — visible streak.',
  },
  {
    key: 'timed_quiz',
    title: 'Timed Passage + Quiz',
    tagline: 'The gold-standard test',
    icon: BookOpenCheck,
    accent: 'amber',
    route: '/learn/timed-quiz',
    body: 'Read a passage under a timer, then answer comprehension questions. Your combined score — comprehension-adjusted WPM — ranks you on the Reader leaderboard.',
    gamified: 'Ranked on comprehension-adjusted score, never raw WPM.',
  },
  {
    key: 'regression',
    title: 'Regression Tracking',
    tagline: 'Smooth Reader badge',
    icon: TrendingUp,
    accent: 'emerald',
    route: '/learn/regression',
    body: 'Awareness of how often you re-tap or scroll back reduces the habit without penalty. Flagged privately so you can see your own trend.',
    gamified: 'Sessions with ≤ 2 backtracks earn the "Smooth Reader" badge.',
  },
  {
    key: 'preview',
    title: 'Preview-then-Read',
    tagline: 'Build a mental map first',
    icon: Eye,
    accent: 'indigo',
    route: '/learn/preview-read',
    body: 'Flash headings and topic sentences for a few seconds, then read the full passage. Trains the skill of building a mental scaffold before diving in.',
    gamified: 'Bonus XP when comprehension beats 80% — technique made visible.',
  },
  {
    key: 'scan',
    title: 'Skim / Scan Drills',
    tagline: 'Match speed to purpose',
    icon: Search,
    accent: 'cyan',
    route: '/learn/scan',
    body: 'Task-driven reading: find one specific fact vs. summarise a scene. Two different reading gears, both trainable.',
    gamified: 'Scan-time logged alongside comprehension — separate leaderboards.',
  },
  {
    key: 'fixations',
    title: 'Progressive Fixations',
    tagline: 'Wider single-glance intake',
    icon: Sparkles,
    accent: 'rose',
    route: '/learn/fixations',
    body: 'Fixed stop-points in a passage that decrease over sessions, forcing wider chunks per fixation. Evidence-grounded alternative to raw peripheral drills.',
    gamified: 'Fixation dots shrink as your chunk width unlocks.',
  },
  {
    key: 'vocab',
    title: 'Vocabulary Reps',
    tagline: 'Compound long-term gains',
    icon: TypeIcon,
    accent: 'amber',
    route: '/learn/vocab',
    body: 'Quick flashcard loop tied to words from the passage you just read. Word-recognition automaticity is what really drives long-term speed.',
    gamified: '3-card decks per passage — same anti-skim scoring as comprehension.',
  },
];

const ACCENT = {
  cyan:    { bg: 'bg-cyan-500/10 hover:bg-cyan-500/20',    border: 'border-cyan-400/25 hover:border-cyan-400/60',    text: 'text-cyan-300',    icon: 'text-cyan-300' },
  fuchsia: { bg: 'bg-fuchsia-500/10 hover:bg-fuchsia-500/20', border: 'border-fuchsia-400/25 hover:border-fuchsia-400/60', text: 'text-fuchsia-300', icon: 'text-fuchsia-300' },
  amber:   { bg: 'bg-amber-500/10 hover:bg-amber-500/20',  border: 'border-amber-400/25 hover:border-amber-400/60',  text: 'text-amber-300',   icon: 'text-amber-300' },
  emerald: { bg: 'bg-emerald-500/10',  border: 'border-emerald-400/20',  text: 'text-emerald-300', icon: 'text-emerald-300' },
  indigo:  { bg: 'bg-indigo-500/10',   border: 'border-indigo-400/20',   text: 'text-indigo-300',  icon: 'text-indigo-300' },
  rose:    { bg: 'bg-rose-500/10',     border: 'border-rose-400/20',     text: 'text-rose-300',    icon: 'text-rose-300' },
};

const Learn = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  // "Bring your own passage" modal state + active-custom banner.
  const [customOpen, setCustomOpen] = useState(false);
  const [activeCustom, setActiveCustom] = useState(null);
  // Recent custom-passage list (last 5, newest first). Tap a row to
  // re-select without re-hitting the LLM.
  const [recentCustom, setRecentCustom] = useState([]);
  const [selectingPid, setSelectingPid] = useState(null);
  const [deletingPid, setDeletingPid] = useState(null);
  const [cyclingLibrary, setCyclingLibrary] = useState(false);
  // Feb 14 2026 — 20-min/day × 7 days LEARN gate. Peek so landing on
  // /learn doesn't burn the clock; drills themselves start it on their
  // first `session/complete` call.
  const { status: trialStatus, blocked: trialBlocked } =
    useTrialGate('learn', { peek: true });

  const refreshRecent = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/learn/custom-passages`);
      if (r.ok) {
        const d = await r.json();
        setRecentCustom(d.passages || []);
      }
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/learn/progress`);
        if (r.ok) setProgress(await r.json());
      } catch { /* non-fatal */ }
    })();
    refreshRecent();
    // Detect any custom passage stashed by the modal so we can flash
    // a banner and give the user a clear "clear it" affordance.
    try {
      const raw = sessionStorage.getItem('learn_custom_passage');
      if (raw) setActiveCustom(JSON.parse(raw));
    } catch { /* private mode */ }
  }, []);

  const clearCustom = () => {
    try { sessionStorage.removeItem('learn_custom_passage'); } catch { /* no-op */ }
    setActiveCustom(null);
  };

  // Re-select a stored custom passage: hydrate its full body (text +
  // questions + vocab) from the detail endpoint, stash in
  // sessionStorage, flip the banner. Cheaper than LLM regeneration.
  const selectRecent = async (passageId) => {
    if (selectingPid || deletingPid) return;
    setSelectingPid(passageId);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/learn/custom-passage/${passageId}`);
      if (!r.ok) throw new Error('Could not load');
      const d = await r.json();
      try { sessionStorage.setItem('learn_custom_passage', JSON.stringify(d)); } catch { /* no-op */ }
      setActiveCustom(d);
    } catch { /* non-fatal */ }
    finally { setSelectingPid(null); }
  };

  // Prune a saved custom passage. Clear the active banner if we just
  // deleted the currently-loaded passage so drills don't grade against
  // a passage the user no longer sees.
  const deleteRecent = async (passageId, ev) => {
    if (ev) { ev.stopPropagation(); ev.preventDefault(); }
    if (deletingPid) return;
    setDeletingPid(passageId);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/learn/custom-passage/${passageId}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      setRecentCustom((cur) => cur.filter((p) => p.passage_id !== passageId));
      if (activeCustom?.passage_id === passageId) clearCustom();
      toast.success('Passage removed');
    } catch (e) {
      toast.error('Could not delete passage');
    } finally {
      setDeletingPid(null);
    }
  };

  // Feb 14 2026 — pull the next unseen library book as a drill passage.
  // Server auto-generates 3 MCQs + 3 vocab items on first materialise,
  // then caches so repeat cycles are instant. Never repeats the same
  // book until the library is exhausted.
  const cycleLibrary = async () => {
    if (cyclingLibrary || trialBlocked) return;
    setCyclingLibrary(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/learn/library-cycle`, { method: 'POST' });
      if (r.status === 402) {
        toast.error('Free trial limit reached. Upgrade to Premium to keep cycling.', {
          action: { label: 'Upgrade', onClick: () => navigate('/premium') },
        });
        return;
      }
      if (r.status === 404) {
        toast.error('No library books have enough text for drills yet.');
        return;
      }
      if (!r.ok) {
        let msg = 'Could not load library passage';
        try { const d = await r.json(); if (d?.detail) msg = d.detail; } catch { /* no-op */ }
        toast.error(msg);
        return;
      }
      const d = await r.json();
      try { sessionStorage.setItem('learn_custom_passage', JSON.stringify(d)); } catch { /* no-op */ }
      setActiveCustom(d);
      // Nudge the recent-list refresh so the strip picks up the new
      // materialised passage on next mount if we choose to surface
      // library items in it later.
      if (d.exhausted) {
        toast.info('Library exhausted — cycling back to earlier picks.');
      } else {
        toast.success(`Loaded "${d.title}" — ${d.remaining} fresh book${d.remaining === 1 ? '' : 's'} left`);
      }
    } catch (e) {
      toast.error(e?.message || 'Could not cycle library');
    } finally {
      setCyclingLibrary(false);
    }
  };

  return (
    <main
      className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32"
      data-testid="learn-page"
    >
      <header className="mb-6 flex items-center gap-3">
        <GraduationCap className="w-7 h-7 text-cyan-300" />
        <div>
          <p className="eyebrow text-cyan-300 mb-1">Reader Skills</p>
          <h1
            className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight leading-none"
            data-testid="learn-page-title"
          >
            Learn to read <span className="italic text-cyan-300">smarter</span>.
          </h1>
        </div>
      </header>

      {/* Feb 14 2026 — 1-day free-trial gate. Renders the paywall
          when the user's Learn trial has expired; otherwise a subtle
          "trial · N left" strip. Premium users see nothing. */}
      <TrialGateBanner status={trialStatus} feature="learn" />

      {/* SmarterCourses — 50 courses across 6 paths. Imported Feb 15 2026
          from the theantagonistari-sudo/smartercourses prototype. Sits
          above the drill grid so users find the structured learning
          path before jumping into the freeform drills. */}
      <button
        type="button"
        onClick={() => navigate('/learn/courses')}
        className="w-full text-left rounded-2xl bg-gradient-to-br from-purple-950/60 via-slate-900 to-slate-950 border border-purple-500/30 hover:border-purple-400/60 p-4 mb-3 transition-colors group"
        data-testid="learn-courses-tile"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="eyebrow text-purple-300 mb-1 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Courses
            </p>
            <p className="font-display text-lg font-black text-white tracking-tight">
              50 courses · 6 paths
            </p>
            <p className="text-xs text-white/60 leading-relaxed mt-1">
              Structured learning across Writer, Reader, Listener, Ambassador, Platform
              and Business paths. Each course is 2 sections + a final exam with real
              XP + coin rewards.
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-purple-300 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </button>

      {/* Progress banner — surfaces the single unifying metric so users
          immediately see what they're optimising for. */}
      <section
        className="rounded-2xl bg-gradient-to-br from-cyan-950/60 via-slate-900 to-slate-950 border border-cyan-500/20 p-5 mb-6"
        data-testid="learn-progress-banner"
      >
        <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-300/80 font-black mb-1 flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5" />
          Comprehension-adjusted WPM
        </p>
        <div className="flex items-end gap-3 flex-wrap">
          <p
            className="font-display text-5xl sm:text-6xl font-black text-white tabular-nums leading-none"
            data-testid="learn-best-adjusted-wpm"
          >
            {progress ? Math.round(progress.best_adjusted_wpm || 0) : '—'}
          </p>
          <div className="pb-1">
            <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Personal best</p>
            <p className="text-xs text-white/70">
              {progress
                ? `${progress.total_sessions} drill${progress.total_sessions === 1 ? '' : 's'} · ${Math.round(progress.avg_comprehension_pct || 0)}% avg comprehension`
                : 'Complete your first drill to unlock.'}
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-[11px] uppercase tracking-widest">
          <span className="text-white/60">
            Chunks unlocked:{' '}
            <span className="text-cyan-300 font-black" data-testid="learn-chunk-unlocked">
              {progress ? progress.chunk_size_unlocked : 2}-word
            </span>
          </span>
          <span className="text-white/60">
            Next pacer target:{' '}
            <span className="text-fuchsia-300 font-black" data-testid="learn-pacer-target">
              {progress ? progress.pacer_target_wpm : 200} WPM
            </span>
          </span>
        </div>
      </section>

      <p className="eyebrow text-white/50 mb-3">Drills</p>

      {/* Feb 13 2026 — "Bring your own passage" card. Opens a modal
          that pastes text or picks a Library ebook, then routes to the
          LLM to auto-generate 3 comprehension MCQs + 3 vocab items.
          The active passage banner below shows when one is loaded and
          gives a one-tap clear. */}
      {activeCustom ? (
        <div
          className="rounded-2xl bg-emerald-500/10 border border-emerald-400/40 p-4 mb-3 flex items-center justify-between gap-3"
          data-testid="learn-custom-passage-active-banner"
        >
          <div className="min-w-0 flex-1">
            <p className="eyebrow text-emerald-300 mb-1 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Custom passage active
            </p>
            <p className="font-display text-base font-black text-white tracking-tight truncate">
              {activeCustom.title || 'Untitled'}
            </p>
            <p className="text-[11px] text-white/50">
              {activeCustom.word_count} words · every drill below will use this text
            </p>
          </div>
          <button
            onClick={clearCustom}
            className="inline-flex items-center gap-1 rounded-lg bg-black/30 hover:bg-black/50 border border-white/10 px-2.5 py-1.5 text-[11px] text-white/70 font-black"
            data-testid="learn-custom-passage-clear"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mb-3">
          <button
            onClick={() => setCustomOpen(true)}
            className="w-full text-left rounded-2xl bg-gradient-to-br from-cyan-950/70 via-slate-900 to-slate-950 border border-cyan-500/30 hover:border-cyan-400/60 p-4 transition-colors group"
            data-testid="learn-custom-passage-open"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="eyebrow text-cyan-300 mb-1 flex items-center gap-1.5">
                  <Wand2 className="w-3.5 h-3.5" /> New
                </p>
                <p className="font-display text-lg font-black text-white tracking-tight">
                  Bring your own passage
                </p>
                <p className="text-xs text-white/60 leading-relaxed mt-1">
                  Paste any text or pick a Library ebook. The AI
                  generates 3 comprehension MCQs + 3 vocab cards so every
                  drill runs on your own reading material.
                </p>
            </div>
            <ChevronRight className="w-5 h-5 text-cyan-300 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </button>
        <button
          onClick={cycleLibrary}
          disabled={cyclingLibrary || trialBlocked}
          className="w-full sm:w-auto rounded-2xl bg-amber-500/15 hover:bg-amber-500/25 disabled:opacity-40 disabled:cursor-not-allowed border border-amber-400/40 hover:border-amber-300/70 px-4 py-3 transition-colors flex items-center justify-center gap-2 text-amber-200 text-sm font-black tracking-tight"
          data-testid="learn-library-cycle"
          title="Load a fresh library passage — no repeats until we run out"
        >
          {cyclingLibrary ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Shuffle className="w-4 h-4" />
          )}
          {cyclingLibrary ? 'Loading library…' : 'Cycle library passage'}
        </button>
        </div>
      )}

      {/* Recent custom passages — one tap re-hydrates a stored passage
          into sessionStorage without re-hitting the LLM. Hides when
          the user has none yet. */}
      {recentCustom.length > 0 && (
        <div className="mb-4" data-testid="learn-recent-custom-strip">
          <p className="eyebrow text-white/50 mb-2 flex items-center gap-1.5">
            <History className="w-3 h-3" /> Recent custom passages
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
            {recentCustom.map((p) => {
              const isActive = activeCustom?.passage_id === p.passage_id;
              const isLoading = selectingPid === p.passage_id;
              const isDeleting = deletingPid === p.passage_id;
              return (
                <div
                  key={p.passage_id}
                  className={`relative flex-shrink-0 w-52 rounded-xl border p-3 transition-colors ${
                    isActive
                      ? 'bg-emerald-500/10 border-emerald-400/50'
                      : 'bg-[#12131A] border-white/10 hover:border-cyan-400/40'
                  } ${isDeleting ? 'opacity-50' : ''}`}
                  data-testid={`learn-recent-custom-${p.passage_id}`}
                >
                  <button
                    type="button"
                    disabled={isLoading || isDeleting}
                    onClick={() => selectRecent(p.passage_id)}
                    className="w-full text-left disabled:cursor-wait"
                    data-testid={`learn-recent-custom-select-${p.passage_id}`}
                  >
                    <p className={`text-[10px] uppercase tracking-[0.2em] font-black mb-1 flex items-center gap-1 ${
                      isActive ? 'text-emerald-300' : 'text-cyan-300/80'
                    }`}>
                      {isLoading ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Loading…</>
                      ) : isActive ? (
                        <><Sparkles className="w-3 h-3" /> Active</>
                      ) : (
                        <>{p.source === 'library' ? 'Library' : 'Paste'}</>
                      )}
                    </p>
                    <p className="font-display text-sm font-black text-white tracking-tight truncate leading-tight pr-6">
                      {p.title}
                    </p>
                    <p className="text-[11px] text-white/45 mt-0.5">
                      {p.word_count} words
                    </p>
                  </button>
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={(ev) => deleteRecent(p.passage_id, ev)}
                    className="absolute top-2 right-2 p-1 rounded-md text-white/40 hover:text-rose-300 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/40"
                    data-testid={`learn-recent-custom-delete-${p.passage_id}`}
                    aria-label="Delete passage"
                  >
                    {isDeleting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {DRILLS.map((d) => {
          const A = ACCENT[d.accent] || ACCENT.cyan;
          const clickable = !!d.route && !trialBlocked;
          const Icon = d.icon;
          const handleClick = () => {
            if (trialBlocked) {
              toast.error('Free trial limit reached. Upgrade to Premium to keep training.', {
                action: { label: 'Upgrade', onClick: () => navigate('/premium') },
              });
              return;
            }
            if (d.route) navigate(d.route);
          };
          return (
            <button
              key={d.key}
              type="button"
              disabled={!d.route}
              onClick={handleClick}
              className={`group text-left rounded-2xl border ${A.border} ${A.bg} p-5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50 ${d.route && !trialBlocked ? '' : 'opacity-60 cursor-not-allowed'}`}
              data-testid={`learn-drill-${d.key}`}
            >
              <div className="flex items-start justify-between mb-3">
                <Icon className={`w-6 h-6 ${A.icon}`} strokeWidth={1.6} />
                {clickable ? (
                  <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white/80 transition-colors" />
                ) : trialBlocked && d.route ? (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-300 font-black">
                    <Lock className="w-3 h-3" /> Premium
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/40 font-black">
                    <Lock className="w-3 h-3" /> Soon
                  </span>
                )}
              </div>
              <p className={`text-[10px] uppercase tracking-[0.25em] font-black ${A.text} mb-1`}>
                {d.tagline}
              </p>
              <h3 className="font-display text-lg font-black text-white tracking-tight leading-tight mb-2">
                {d.title}
              </h3>
              <p className="text-xs text-white/60 leading-relaxed mb-2">{d.body}</p>
              <p className="text-[11px] text-white/40 italic">{d.gamified}</p>
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-white/40 mt-6 text-center leading-relaxed">
        Every drill is scored on <span className="text-cyan-300 font-bold">comprehension-adjusted WPM</span> —
        so speed never comes at the cost of retention. Skim a passage and your score drops with it.
      </p>

      <CustomPassageModal open={customOpen} onClose={() => {
        setCustomOpen(false);
        // Re-read sessionStorage so the banner surfaces immediately
        // when the modal saved a new custom passage before closing.
        try {
          const raw = sessionStorage.getItem('learn_custom_passage');
          if (raw) setActiveCustom(JSON.parse(raw));
        } catch { /* no-op */ }
        // Newly generated passage should show up in the strip.
        refreshRecent();
      }} />
    </main>
  );
};

export default Learn;
